import { useCallback, useEffect, useRef } from 'react';

import { getAppStore } from '@/store';
import sendAiGenScreenActionAnalytics from '@/utils/analytics/sendAiGenScreenActionAnalytics';
import { AiGenScreenAction, ScreenType } from '@/types/analytics';

interface SendPresetFeedOpenAnalyticsParams {
  slug: string;
  category: string;
}

interface FeedSwipeTracking {
  activeIndex: number;
  hasUserScrolled: boolean;
  activeSlug: string | undefined;
}

const usePresetFeedAnalytics = (
  swipeTracking?: FeedSwipeTracking
): {
  sendPresetFeedOpenAnalytics: (
    params: SendPresetFeedOpenAnalyticsParams
  ) => void;
} => {
  const sendPresetFeedOpenAnalytics = useCallback(
    ({ slug, category }: SendPresetFeedOpenAnalyticsParams): void => {
      sendAiGenScreenActionAnalytics({
        action: AiGenScreenAction.OPEN,
        screen_type: ScreenType.PRESET_FEED,
        source: 'click',
        settings: { category, id: slug },
      });
    },
    []
  );

  const activeIndex = swipeTracking?.activeIndex;
  const hasUserScrolled = swipeTracking?.hasUserScrolled;
  const activeSlug = swipeTracking?.activeSlug;

  const prevIndexRef = useRef(activeIndex);
  useEffect(() => {
    if (activeIndex === undefined) return;
    const prevIndex = prevIndexRef.current;
    prevIndexRef.current = activeIndex;
    if (prevIndex === undefined || activeIndex === prevIndex) return;
    if (!hasUserScrolled || !activeSlug) return;
    sendAiGenScreenActionAnalytics({
      action:
        activeIndex > prevIndex
          ? AiGenScreenAction.SWIPE_UP
          : AiGenScreenAction.SWIPE_DOWN,
      screen_type: ScreenType.PRESET_FEED,
      settings: { category: getAppStore().activeTabSlug ?? '', id: activeSlug },
    });
  }, [activeIndex, hasUserScrolled, activeSlug]);

  return { sendPresetFeedOpenAnalytics };
};

export default usePresetFeedAnalytics;
